import { useMemo, useState } from "react";
import {
  BookOpenCheck,
  FileClock,
  ClipboardList,
  Edit3,
  Trash2,
} from "lucide-react";
import { Iso2700127701ControlModificacionDeleteModal } from "../../../components/control-modificaciones/Iso2700127701ControlModificacionDeleteModal";
import { Iso2700127701ControlModificacionFormModal } from "../../../components/control-modificaciones/Iso2700127701ControlModificacionFormModal";
import { Iso2700127701ControlModificacionesTable } from "../../../components/control-modificaciones/Iso2700127701ControlModificacionesTable";
import {
  Iso2700127701Button,
  Iso2700127701ConfirmDialog,
  Iso2700127701DataTable,
  Iso2700127701Filters,
  Iso2700127701IconButton,
  Iso2700127701InputField,
  Iso2700127701Modal,
  Iso2700127701OverviewCard,
  Iso2700127701ScrollableCell,
  Iso2700127701SectionHeader,
  Iso2700127701SelectField,
  Iso2700127701Tabs,
  Iso2700127701TextareaField,
  type Iso2700127701TableColumn,
} from "../../../components/shared";
import { useIso2700127701DebouncedValue } from "../../../components/shared/useIso2700127701FilteredRequest";
import type {
  Iso2700127701ControlModificacion,
  Iso2700127701ControlModificacionPayload,
} from "../../../types/control-modificaciones";
import { createEmptyControlModificacionForm } from "../../../types/control-modificaciones";
import type {
  Iso2700127701PlanAnexoA,
  Iso2700127701PlanPayload,
  Iso2700127701SoaControl,
  Iso2700127701SoaPayload,
} from "../../../types/iso2700127701";
import {
  createEmptyPlanForm,
  createEmptySoaForm,
  defaultSoaFilters,
} from "../../../types/iso2700127701";
import {
  createIso2700127701PlanAnexoA,
  createIso2700127701SoaControl,
  createIso2700127701SoaControlModificacion,
  deleteIso2700127701PlanAnexoA,
  deleteIso2700127701SoaControl,
  deleteIso2700127701SoaControlModificacion,
  updateIso2700127701PlanAnexoA,
  updateIso2700127701SoaControl,
  updateIso2700127701SoaControlModificacion,
} from "../api/soa";
import { useIso2700127701Soa } from "../hooks/useIso2700127701Soa";

type Props = { onBack: () => void };
type ActiveTab = "soa" | "plan" | "control";
type SortDirection = "codigo_asc" | "codigo_desc";
type PlanFilters = { search: string; aplica: "" | "true" | "false"; sort: SortDirection };
type ControlFilters = { search: string; sort: "fecha_desc" | "fecha_asc" };

type SoaFormState =
  | { open: false }
  | {
      open: true;
      mode: "create" | "edit";
      row: Iso2700127701SoaControl | null;
      value: Iso2700127701SoaPayload;
    };

type PlanFormState =
  | { open: false }
  | {
      open: true;
      mode: "create" | "edit";
      row: Iso2700127701PlanAnexoA | null;
      value: Iso2700127701PlanPayload;
    };

type ControlFormState =
  | { open: false }
  | {
      open: true;
      mode: "create" | "edit";
      row: Iso2700127701ControlModificacion | null;
      value: Iso2700127701ControlModificacionPayload;
    };

const defaultPlanFilters: PlanFilters = { search: "", aplica: "", sort: "codigo_asc" };
const defaultControlFilters: ControlFilters = { search: "", sort: "fecha_desc" };

function textValue(value: string | null | undefined) {
  return value?.trim() ? value : <span className="iso27001-muted-copy">Sin contenido</span>;
}

function appliesBadge(value: boolean | null) {
  if (value === true) return <span className="iso27001-status-badge success">Sí</span>;
  if (value === false) return <span className="iso27001-status-badge danger">No</span>;
  return <span className="iso27001-status-badge neutral">Sin definir</span>;
}

function soaPayloadFromRow(row: Iso2700127701SoaControl): Iso2700127701SoaPayload {
  return {
    serie: row.serie,
    codigo_grupo: row.codigo_grupo,
    grupo: row.grupo,
    codigo_control: row.codigo_control,
    control: row.control,
    detalle: row.detalle,
    aplica: row.aplica,
    justificacion_soporte_documental: row.justificacion_soporte_documental,
    soporte_documental: row.soporte_documental,
    evidencia_inproeco: row.evidencia_inproeco,
    actualizacion_2026: row.actualizacion_2026,
    observaciones: row.observaciones,
    responsables: row.responsables,
    comentarios_inproeco: row.comentarios_inproeco,
  };
}

function planPayloadFromRow(row: Iso2700127701PlanAnexoA): Iso2700127701PlanPayload {
  return {
    codigo_control: row.codigo_control,
    intervencion: row.intervencion,
    detalle: row.detalle,
    aplica: row.aplica,
    seguimiento: row.seguimiento,
  };
}

export function Iso2700127701SoaSection({ onBack }: Props) {
  const [activeTab, setActiveTab] = useState<ActiveTab>("soa");
  const [filters, setFilters] = useState(defaultSoaFilters);
  const [soaSort, setSoaSort] = useState<SortDirection>("codigo_asc");
  const [planFilters, setPlanFilters] = useState<PlanFilters>(defaultPlanFilters);
  const [controlFilters, setControlFilters] = useState<ControlFilters>(defaultControlFilters);
  const {
    soaRows,
    planRows,
    controlModificaciones,
    filterOptions,
    meta,
    loading,
    error,
    reload,
  } = useIso2700127701Soa(filters);

  const [soaForm, setSoaForm] = useState<SoaFormState>({ open: false });
  const [planForm, setPlanForm] = useState<PlanFormState>({ open: false });
  const [deleteSoaRow, setDeleteSoaRow] = useState<Iso2700127701SoaControl | null>(null);
  const [deletePlanRow, setDeletePlanRow] = useState<Iso2700127701PlanAnexoA | null>(null);
  const [controlForm, setControlForm] = useState<ControlFormState>({ open: false });
  const [controlDeleteRow, setControlDeleteRow] = useState<Iso2700127701ControlModificacion | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const debouncedPlanSearch = useIso2700127701DebouncedValue(planFilters.search.trim(), 300).toLowerCase();
  const debouncedControlSearch = useIso2700127701DebouncedValue(controlFilters.search.trim(), 300).toLowerCase();

  const sortedSoaRows = useMemo(() => {
    return [...soaRows].sort((left, right) => {
      const comparison = left.codigo_control.localeCompare(right.codigo_control, "es", { numeric: true });
      return soaSort === "codigo_asc" ? comparison : -comparison;
    });
  }, [soaRows, soaSort]);

  const filteredPlanRows = useMemo(() => {
    const term = debouncedPlanSearch;
    const filtered = planRows.filter((row) => {
      const matchesSearch = !term || [row.codigo_control, row.intervencion, row.detalle, row.seguimiento]
        .some((value) => value?.toLowerCase().includes(term));
      const matchesApplies = !planFilters.aplica
        || String(row.aplica) === planFilters.aplica;
      return matchesSearch && matchesApplies;
    });

    return filtered.sort((left, right) => {
      const comparison = left.codigo_control.localeCompare(right.codigo_control, "es", { numeric: true });
      return planFilters.sort === "codigo_asc" ? comparison : -comparison;
    });
  }, [debouncedPlanSearch, planFilters.aplica, planFilters.sort, planRows]);

  const filteredControlRows = useMemo(() => {
    const term = debouncedControlSearch;
    const filtered = controlModificaciones.filter((row) => (
      !term
      || row.fecha.toLowerCase().includes(term)
      || row.modificaciones_realizadas.toLowerCase().includes(term)
    ));

    return filtered.sort((left, right) => {
      const comparison = left.fecha.localeCompare(right.fecha, "es", { numeric: true });
      return controlFilters.sort === "fecha_asc" ? comparison : -comparison;
    });
  }, [controlFilters.sort, controlModificaciones, debouncedControlSearch]);


  const submitSoa = async () => {
    if (!soaForm.open) return;
    setSubmitting(true);
    setFormError(null);
    try {
      if (soaForm.mode === "create") {
        await createIso2700127701SoaControl(soaForm.value);
      } else if (soaForm.row) {
        await updateIso2700127701SoaControl(soaForm.row.id, soaForm.value);
      }
      setSoaForm({ open: false });
      await reload();
    } catch {
      setFormError("No se pudo guardar el control SOA.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitPlan = async () => {
    if (!planForm.open) return;
    setSubmitting(true);
    setFormError(null);
    try {
      if (planForm.mode === "create") {
        await createIso2700127701PlanAnexoA(planForm.value);
      } else if (planForm.row) {
        await updateIso2700127701PlanAnexoA(planForm.row.id, planForm.value);
      }
      setPlanForm({ open: false });
      await reload();
    } catch {
      setFormError("No se pudo guardar el registro de Plan Anexo A.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitControl = async () => {
    if (!controlForm.open) return;
    setSubmitting(true);
    setFormError(null);
    try {
      if (controlForm.mode === "create") {
        await createIso2700127701SoaControlModificacion(controlForm.value);
      } else if (controlForm.row) {
        await updateIso2700127701SoaControlModificacion(controlForm.row.id, controlForm.value);
      }
      setControlForm({ open: false });
      await reload();
    } catch {
      setFormError("No se pudo guardar el control documental.");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteSoa = async () => {
    if (!deleteSoaRow) return;
    setSubmitting(true);
    try {
      await deleteIso2700127701SoaControl(deleteSoaRow.id);
      setDeleteSoaRow(null);
      await reload();
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeletePlan = async () => {
    if (!deletePlanRow) return;
    setSubmitting(true);
    try {
      await deleteIso2700127701PlanAnexoA(deletePlanRow.id);
      setDeletePlanRow(null);
      await reload();
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteControl = async () => {
    if (!controlDeleteRow) return;
    setSubmitting(true);
    try {
      await deleteIso2700127701SoaControlModificacion(controlDeleteRow.id);
      setControlDeleteRow(null);
      await reload();
    } finally {
      setSubmitting(false);
    }
  };

  const soaColumns: Iso2700127701TableColumn<Iso2700127701SoaControl>[] = [
    {
      key: "acciones",
      header: "Acciones",
      className: "iso27001-actions-column",
      render: (row) => (
        <div className="iso27001-row-actions">
          <Iso2700127701IconButton
            label="Editar control SOA"
            icon={<Edit3 size={15} />}
            onClick={() => {
              setFormError(null);
              setSoaForm({ open: true, mode: "edit", row, value: soaPayloadFromRow(row) });
            }}
          />
          <Iso2700127701IconButton
            label="Eliminar control SOA"
            icon={<Trash2 size={15} />}
            danger
            onClick={() => setDeleteSoaRow(row)}
          />
        </div>
      ),
    },
    { key: "codigo", header: "Código", className: "iso27001-code-column", render: (row) => row.codigo_control },
    {
      key: "grupo",
      header: "Grupo",
      className: "iso27001-medium-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.grupo ?? undefined}>
          {textValue(row.grupo)}
        </Iso2700127701ScrollableCell>
      ),
    },
    {
      key: "control",
      header: "Control",
      className: "iso27001-long-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.control}>
          {row.control}
        </Iso2700127701ScrollableCell>
      ),
    },
    {
      key: "detalle",
      header: "Detalle",
      className: "iso27001-long-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.detalle ?? undefined}>
          {textValue(row.detalle)}
        </Iso2700127701ScrollableCell>
      ),
    },
    { key: "aplica", header: "Aplica", className: "iso27001-status-column", render: (row) => appliesBadge(row.aplica) },
    {
      key: "justificacion",
      header: "Justificación / soporte documental",
      className: "iso27001-extra-long-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.justificacion_soporte_documental ?? undefined}>
          {textValue(row.justificacion_soporte_documental)}
        </Iso2700127701ScrollableCell>
      ),
    },
    {
      key: "soporte",
      header: "Soporte documental",
      className: "iso27001-long-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.soporte_documental ?? undefined}>
          {textValue(row.soporte_documental)}
        </Iso2700127701ScrollableCell>
      ),
    },
    {
      key: "evidencia",
      header: "Evidencia INPROECO",
      className: "iso27001-long-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.evidencia_inproeco ?? undefined}>
          {textValue(row.evidencia_inproeco)}
        </Iso2700127701ScrollableCell>
      ),
    },
    {
      key: "actualizacion",
      header: "Actualización 2026",
      className: "iso27001-medium-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.actualizacion_2026 ?? undefined}>
          {textValue(row.actualizacion_2026)}
        </Iso2700127701ScrollableCell>
      ),
    },
    {
      key: "observaciones",
      header: "Observaciones",
      className: "iso27001-long-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.observaciones ?? undefined}>
          {textValue(row.observaciones)}
        </Iso2700127701ScrollableCell>
      ),
    },
    {
      key: "responsables",
      header: "Responsables",
      className: "iso27001-medium-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.responsables ?? undefined}>
          {textValue(row.responsables)}
        </Iso2700127701ScrollableCell>
      ),
    },
    {
      key: "comentarios",
      header: "Comentarios INPROECO",
      className: "iso27001-long-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.comentarios_inproeco ?? undefined}>
          {textValue(row.comentarios_inproeco)}
        </Iso2700127701ScrollableCell>
      ),
    },
  ];

  const planColumns: Iso2700127701TableColumn<Iso2700127701PlanAnexoA>[] = [
    {
      key: "acciones",
      header: "Acciones",
      className: "iso27001-actions-column",
      render: (row) => (
        <div className="iso27001-row-actions">
          <Iso2700127701IconButton
            label="Editar registro de Plan Anexo A"
            icon={<Edit3 size={15} />}
            onClick={() => {
              setFormError(null);
              setPlanForm({ open: true, mode: "edit", row, value: planPayloadFromRow(row) });
            }}
          />
          <Iso2700127701IconButton
            label="Eliminar registro de Plan Anexo A"
            icon={<Trash2 size={15} />}
            danger
            onClick={() => setDeletePlanRow(row)}
          />
        </div>
      ),
    },
    { key: "codigo", header: "Código", className: "iso27001-code-column", render: (row) => row.codigo_control },
    {
      key: "intervencion",
      header: "Intervención",
      className: "iso27001-long-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.intervencion}>
          {row.intervencion}
        </Iso2700127701ScrollableCell>
      ),
    },
    {
      key: "detalle",
      header: "Detalle",
      className: "iso27001-extra-long-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.detalle ?? undefined}>
          {textValue(row.detalle)}
        </Iso2700127701ScrollableCell>
      ),
    },
    { key: "aplica", header: "Aplica", className: "iso27001-status-column", render: (row) => appliesBadge(row.aplica) },
    {
      key: "seguimiento",
      header: "Seguimiento",
      className: "iso27001-extra-long-text-column",
      render: (row) => (
        <Iso2700127701ScrollableCell title={row.seguimiento ?? undefined}>
          {textValue(row.seguimiento)}
        </Iso2700127701ScrollableCell>
      ),
    },
  ];

  return (
    <section className="iso27001-section-shell">
      <Iso2700127701SectionHeader
        eyebrow="Declaración de aplicabilidad"
        title="SOA"
        description="Gestión de controles, aplicabilidad, evidencias y planificación del Anexo A."
        onBack={onBack}
        onRefresh={() => void reload()}
        refreshing={loading}
      />

      <div className="iso27001-overview-grid">
        <Iso2700127701OverviewCard
          icon={BookOpenCheck}
          label="Controles SOA"
          value={loading ? "..." : meta.soa_total}
        />
        <Iso2700127701OverviewCard
          icon={ClipboardList}
          label="Plan Anexo A"
          value={loading ? "..." : meta.plan_anexo_a_total}
        />
        <Iso2700127701OverviewCard
          icon={FileClock}
          label="Control documental"
          value={loading ? "..." : controlModificaciones.length}
        />
      </div>

      <Iso2700127701Tabs<ActiveTab>
        activeTab={activeTab}
        onChange={setActiveTab}
        ariaLabel="Tablas de la sección SOA"
        tabs={[
          { id: "soa", label: "SOA", count: soaRows.length },
          { id: "plan", label: "Plan Anexo A", count: planRows.length },
          { id: "control", label: "Control documental", count: controlModificaciones.length },
        ]}
      />

      {activeTab === "soa" ? (
        <>
          <Iso2700127701Filters
            title="Filtrar controles SOA"
            description="Busca por contenido y combina los selectores disponibles."
            visibleCount={sortedSoaRows.length}
            totalCount={meta.soa_total}
            onReset={() => {
              setFilters(defaultSoaFilters);
              setSoaSort("codigo_asc");
            }}
          >
            <Iso2700127701InputField
              label="Búsqueda"
              placeholder="Control, detalle, evidencia..."
              value={filters.search}
              onChange={(event) => setFilters({ ...filters, search: event.target.value })}
            />
            <Iso2700127701InputField
              label="Código de control"
              placeholder="Ej. 5.01"
              value={filters.codigo_control}
              onChange={(event) => setFilters({ ...filters, codigo_control: event.target.value })}
            />
            <Iso2700127701SelectField
              label="Grupo"
              value={filters.grupo}
              onChange={(event) => setFilters({ ...filters, grupo: event.target.value })}
            >
              <option value="">Todos los grupos</option>
              {filterOptions.grupos.map((grupo) => <option key={grupo} value={grupo}>{grupo}</option>)}
            </Iso2700127701SelectField>
            <Iso2700127701SelectField
              label="Aplicabilidad"
              value={filters.aplica}
              onChange={(event) => setFilters({ ...filters, aplica: event.target.value as typeof filters.aplica })}
            >
              <option value="">Todos</option>
              <option value="true">Sí</option>
              <option value="false">No</option>
            </Iso2700127701SelectField>
            <Iso2700127701SelectField
              label="Responsable"
              value={filters.responsables}
              onChange={(event) => setFilters({ ...filters, responsables: event.target.value })}
            >
              <option value="">Todos los responsables</option>
              {filterOptions.responsables.map((responsable) => (
                <option key={responsable} value={responsable}>{responsable}</option>
              ))}
            </Iso2700127701SelectField>
            <Iso2700127701SelectField
              label="Actualización 2026"
              value={filters.actualizacion_2026}
              onChange={(event) => setFilters({ ...filters, actualizacion_2026: event.target.value })}
            >
              <option value="">Todas</option>
              {filterOptions.actualizaciones_2026.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </Iso2700127701SelectField>
            <Iso2700127701SelectField
              label="Ordenación"
              value={soaSort}
              onChange={(event) => setSoaSort(event.target.value as SortDirection)}
            >
              <option value="codigo_asc">Código ascendente</option>
              <option value="codigo_desc">Código descendente</option>
            </Iso2700127701SelectField>
          </Iso2700127701Filters>

          <Iso2700127701DataTable
            title="Declaración de aplicabilidad"
            countLabel={`${sortedSoaRows.length} controles`}
            rows={sortedSoaRows}
            columns={soaColumns}
            getRowKey={(row) => row.id}
            loading={loading}
            error={error}
            emptyTitle="Sin controles SOA"
            emptyDescription="No hay registros que coincidan con los filtros actuales."
            createLabel="Añadir control"
            onCreate={() => {
              setFormError(null);
              setSoaForm({ open: true, mode: "create", row: null, value: createEmptySoaForm() });
            }}
            onRetry={() => void reload()}
          />
        </>
      ) : null}

      {activeTab === "plan" ? (
        <>
          <Iso2700127701Filters
            title="Filtrar Plan Anexo A"
            visibleCount={filteredPlanRows.length}
            totalCount={planRows.length}
            onReset={() => setPlanFilters(defaultPlanFilters)}
          >
            <Iso2700127701InputField
              label="Búsqueda"
              placeholder="Código, intervención, detalle..."
              value={planFilters.search}
              onChange={(event) => setPlanFilters({ ...planFilters, search: event.target.value })}
            />
            <Iso2700127701SelectField
              label="Aplicabilidad"
              value={planFilters.aplica}
              onChange={(event) => setPlanFilters({ ...planFilters, aplica: event.target.value as PlanFilters["aplica"] })}
            >
              <option value="">Todos</option>
              <option value="true">Sí</option>
              <option value="false">No</option>
            </Iso2700127701SelectField>
            <Iso2700127701SelectField
              label="Ordenación"
              value={planFilters.sort}
              onChange={(event) => setPlanFilters({ ...planFilters, sort: event.target.value as SortDirection })}
            >
              <option value="codigo_asc">Código ascendente</option>
              <option value="codigo_desc">Código descendente</option>
            </Iso2700127701SelectField>
          </Iso2700127701Filters>

          <Iso2700127701DataTable
            title="Plan Anexo A"
            countLabel={`${filteredPlanRows.length} controles`}
            rows={filteredPlanRows}
            columns={planColumns}
            getRowKey={(row) => row.id}
            loading={loading}
            error={error}
            emptyTitle="Sin registros del Plan Anexo A"
            emptyDescription="No hay registros que coincidan con los filtros actuales."
            createLabel="Añadir registro"
            onCreate={() => {
              setFormError(null);
              setPlanForm({ open: true, mode: "create", row: null, value: createEmptyPlanForm() });
            }}
            onRetry={() => void reload()}
          />
        </>
      ) : null}

      {activeTab === "control" ? (
        <>
          <Iso2700127701Filters
            title="Filtrar control documental"
            visibleCount={filteredControlRows.length}
            totalCount={controlModificaciones.length}
            onReset={() => setControlFilters(defaultControlFilters)}
          >
            <Iso2700127701InputField
              label="Búsqueda"
              placeholder="Fecha o modificación..."
              value={controlFilters.search}
              onChange={(event) => setControlFilters({ ...controlFilters, search: event.target.value })}
            />
            <Iso2700127701SelectField
              label="Ordenación"
              value={controlFilters.sort}
              onChange={(event) => setControlFilters({ ...controlFilters, sort: event.target.value as ControlFilters["sort"] })}
            >
              <option value="fecha_desc">Fecha descendente</option>
              <option value="fecha_asc">Fecha ascendente</option>
            </Iso2700127701SelectField>
          </Iso2700127701Filters>

          <Iso2700127701ControlModificacionesTable
            rows={filteredControlRows}
            loading={loading}
            error={error}
            onRetry={() => void reload()}
            onCreate={() => {
              setFormError(null);
              setControlForm({ open: true, mode: "create", row: null, value: createEmptyControlModificacionForm() });
            }}
            onEdit={(row) => {
              setFormError(null);
              setControlForm({
                open: true,
                mode: "edit",
                row,
                value: {
                  fecha: row.fecha,
                  modificaciones_realizadas: row.modificaciones_realizadas,
                },
              });
            }}
            onDelete={setControlDeleteRow}
          />
        </>
      ) : null}

      <SoaFormModal
        state={soaForm}
        submitting={submitting}
        error={formError}
        onClose={() => setSoaForm({ open: false })}
        onChange={(value) => soaForm.open && setSoaForm({ ...soaForm, value })}
        onSubmit={submitSoa}
      />
      <PlanFormModal
        state={planForm}
        submitting={submitting}
        error={formError}
        onClose={() => setPlanForm({ open: false })}
        onChange={(value) => planForm.open && setPlanForm({ ...planForm, value })}
        onSubmit={submitPlan}
      />
      <Iso2700127701ControlModificacionFormModal
        open={controlForm.open}
        title={controlForm.open && controlForm.mode === "edit" ? "Editar modificación" : "Nueva modificación"}
        value={controlForm.open ? controlForm.value : createEmptyControlModificacionForm()}
        submitting={submitting}
        error={formError}
        onClose={() => setControlForm({ open: false })}
        onChange={(value) => controlForm.open && setControlForm({ ...controlForm, value })}
        onSubmit={submitControl}
      />
      <Iso2700127701ConfirmDialog
        open={deleteSoaRow !== null}
        title="Eliminar control SOA"
        description={deleteSoaRow ? `Se eliminará el control ${deleteSoaRow.codigo_control}: ${deleteSoaRow.control}.` : ""}
        confirming={submitting}
        onClose={() => setDeleteSoaRow(null)}
        onConfirm={confirmDeleteSoa}
      />
      <Iso2700127701ConfirmDialog
        open={deletePlanRow !== null}
        title="Eliminar registro del Plan Anexo A"
        description={deletePlanRow ? `Se eliminará el registro ${deletePlanRow.codigo_control}: ${deletePlanRow.intervencion}.` : ""}
        confirming={submitting}
        onClose={() => setDeletePlanRow(null)}
        onConfirm={confirmDeletePlan}
      />
      <Iso2700127701ControlModificacionDeleteModal
        row={controlDeleteRow}
        submitting={submitting}
        onClose={() => setControlDeleteRow(null)}
        onConfirm={confirmDeleteControl}
      />
    </section>
  );
}

type SoaFormModalProps = {
  state: SoaFormState;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onChange: (value: Iso2700127701SoaPayload) => void;
  onSubmit: () => void;
};

function SoaFormModal({ state, submitting, error, onClose, onChange, onSubmit }: SoaFormModalProps) {
  const value = state.open ? state.value : createEmptySoaForm();
  return (
    <Iso2700127701Modal
      open={state.open}
      title={state.open && state.mode === "edit" ? "Editar control SOA" : "Nuevo control SOA"}
      description="Serie y código de grupo se conservan en base de datos, pero no se muestran ni se editan en esta vista."
      size="wide"
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Iso2700127701Button variant="secondary" onClick={onClose} disabled={submitting}>Cancelar</Iso2700127701Button>
          <Iso2700127701Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? "Guardando..." : "Guardar control"}
          </Iso2700127701Button>
        </>
      }
    >
      <div className="iso27001-form-grid">
        <Iso2700127701InputField label="Grupo" value={value.grupo ?? ""} onChange={(event) => onChange({ ...value, grupo: event.target.value })} />
        <Iso2700127701InputField label="Código de control" value={value.codigo_control} required onChange={(event) => onChange({ ...value, codigo_control: event.target.value })} />
        <Iso2700127701TextareaField label="Control" value={value.control} required rows={3} fullWidth onChange={(event) => onChange({ ...value, control: event.target.value })} />
        <Iso2700127701TextareaField label="Detalle" value={value.detalle ?? ""} rows={4} fullWidth onChange={(event) => onChange({ ...value, detalle: event.target.value })} />
        <Iso2700127701SelectField label="Aplica" value={value.aplica === null ? "" : String(value.aplica)} onChange={(event) => onChange({ ...value, aplica: event.target.value === "" ? null : event.target.value === "true" })}>
          <option value="">Sin definir</option>
          <option value="true">Sí</option>
          <option value="false">No</option>
        </Iso2700127701SelectField>
        <Iso2700127701InputField label="Actualización 2026" value={value.actualizacion_2026 ?? ""} onChange={(event) => onChange({ ...value, actualizacion_2026: event.target.value })} />
        <Iso2700127701TextareaField label="Justificación / soporte documental" value={value.justificacion_soporte_documental ?? ""} rows={4} fullWidth onChange={(event) => onChange({ ...value, justificacion_soporte_documental: event.target.value })} />
        <Iso2700127701TextareaField label="Soporte documental" value={value.soporte_documental ?? ""} rows={4} fullWidth onChange={(event) => onChange({ ...value, soporte_documental: event.target.value })} />
        <Iso2700127701TextareaField label="Evidencia INPROECO" value={value.evidencia_inproeco ?? ""} rows={4} fullWidth onChange={(event) => onChange({ ...value, evidencia_inproeco: event.target.value })} />
        <Iso2700127701InputField label="Responsables" value={value.responsables ?? ""} onChange={(event) => onChange({ ...value, responsables: event.target.value })} />
        <Iso2700127701TextareaField label="Observaciones" value={value.observaciones ?? ""} rows={4} fullWidth onChange={(event) => onChange({ ...value, observaciones: event.target.value })} />
        <Iso2700127701TextareaField label="Comentarios INPROECO" value={value.comentarios_inproeco ?? ""} rows={4} fullWidth onChange={(event) => onChange({ ...value, comentarios_inproeco: event.target.value })} />
      </div>
      {error ? <div className="iso27001-form-error">{error}</div> : null}
    </Iso2700127701Modal>
  );
}

type PlanFormModalProps = {
  state: PlanFormState;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onChange: (value: Iso2700127701PlanPayload) => void;
  onSubmit: () => void;
};

function PlanFormModal({ state, submitting, error, onClose, onChange, onSubmit }: PlanFormModalProps) {
  const value = state.open ? state.value : createEmptyPlanForm();
  return (
    <Iso2700127701Modal
      open={state.open}
      title={state.open && state.mode === "edit" ? "Editar Plan Anexo A" : "Nuevo registro de Plan Anexo A"}
      description="Completa la intervención, aplicabilidad y seguimiento del control."
      size="wide"
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Iso2700127701Button variant="secondary" onClick={onClose} disabled={submitting}>Cancelar</Iso2700127701Button>
          <Iso2700127701Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? "Guardando..." : "Guardar registro"}
          </Iso2700127701Button>
        </>
      }
    >
      <div className="iso27001-form-grid">
        <Iso2700127701InputField label="Código de control" value={value.codigo_control} required onChange={(event) => onChange({ ...value, codigo_control: event.target.value })} />
        <Iso2700127701SelectField label="Aplica" value={value.aplica === null ? "" : String(value.aplica)} onChange={(event) => onChange({ ...value, aplica: event.target.value === "" ? null : event.target.value === "true" })}>
          <option value="">Sin definir</option>
          <option value="true">Sí</option>
          <option value="false">No</option>
        </Iso2700127701SelectField>
        <Iso2700127701TextareaField label="Intervención" value={value.intervencion} required rows={4} fullWidth onChange={(event) => onChange({ ...value, intervencion: event.target.value })} />
        <Iso2700127701TextareaField label="Detalle" value={value.detalle ?? ""} rows={5} fullWidth onChange={(event) => onChange({ ...value, detalle: event.target.value })} />
        <Iso2700127701TextareaField label="Seguimiento" value={value.seguimiento ?? ""} rows={5} fullWidth onChange={(event) => onChange({ ...value, seguimiento: event.target.value })} />
      </div>
      {error ? <div className="iso27001-form-error">{error}</div> : null}
    </Iso2700127701Modal>
  );
}
